import { FileAudio, X } from "lucide-react";
import { useMemo, useState } from "react";
import { ArchVizApp } from "@/components/archviz/ArchVizApp";
import { VizHeaderControls } from "@/components/archviz/VizHeaderControls";
import { ReadOnlyMediaPanel } from "@/components/solutions/MediaPanel";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useSharePublic } from "@/hooks/use-shares";
import type { ArchitectureData } from "@/lib/arch-parser";

export function SharedSolutionPage({ token }: { token: string }) {
  const { data, isLoading, isError, error } = useSharePublic(token);
  const [mediaOpen, setMediaOpen] = useState(false);

  const arch = useMemo(() => {
    if (!data?.parsedData) return null;
    return data.parsedData as ArchitectureData;
  }, [data]);

  const mediaLinks = data?.mediaLinks ?? [];

  if (isLoading) {
    return (
      <div className="min-h-screen p-6 space-y-3">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-[60vh] w-full" />
      </div>
    );
  }

  if (isError || !data || !arch) {
    const msg = error instanceof Error ? error.message : "Link not available";
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center space-y-2">
          <h1 className="text-2xl">Link unavailable</h1>
          <p className="text-sm text-muted-foreground">{msg}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col relative">
      {mediaOpen && (
        <div className="fixed inset-0 z-30 bg-background/80 backdrop-blur flex items-center justify-center p-4">
          <div className="bg-card border rounded-lg max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6 space-y-4 relative">
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-2 right-2"
              onClick={() => setMediaOpen(false)}
            >
              <X className="h-4 w-4" />
            </Button>
            <ReadOnlyMediaPanel links={mediaLinks} />
          </div>
        </div>
      )}
      <ArchVizApp
        architecture={arch}
        readOnly
        extras={{ commentsHidden: true }}
        header={
          <header className="flex items-center gap-3 px-4 py-2.5 border-b bg-card/80 backdrop-blur">
            <div className="min-w-0 shrink-0">
              <div className="text-base truncate">{data.solution.name}</div>
              <div className="text-[11px] text-muted-foreground">
                v{data.version.versionNumber}
                {data.version.label ? ` · ${data.version.label}` : ""} · Shared view
              </div>
            </div>
            <span className="h-4 w-px bg-border max-md:hidden" aria-hidden="true" />
            <VizHeaderControls />
            <div className="flex items-center gap-3 shrink-0">
              {mediaLinks.length > 0 && (
                <Button variant="outline" size="sm" onClick={() => setMediaOpen(true)}>
                  <FileAudio className="h-3.5 w-3.5 mr-1.5" />
                  Media ({mediaLinks.length})
                </Button>
              )}
              <a
                href="https://nymbl.app"
                target="_blank"
                rel="noreferrer"
                className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
              >
                Powered by <span className="font-bold text-foreground">Nymbl</span>
              </a>
            </div>
          </header>
        }
      />
    </div>
  );
}
